import React from "react"
import { Radio, Slider } from "antd"
import Strings from "utils/strings"

// Same size presets as the picking apps (dingoo-mobile parcel_indirect_utils.dart).
// The dimension is the id prefix the API writes: `${dimension}${orderId}`.
export type ClassificationTab = "bag" | "outOfFormat"

const SIZE_LABELS = [
	"sizeVerySmall",
	"sizeSmall",
	"sizeMedium",
	"sizeLarge",
	"sizeVeryLarge",
] as const

export const BAG_DIMENSIONS = ["#1", "#2", "#3", "#4", "#5"]
export const OUT_OF_FORMAT_DIMENSIONS = ["X1X", "X2X", "X3X", "X4X", "X5X"]

const dimensionsFor = (tab: ClassificationTab) =>
	tab === "bag" ? BAG_DIMENSIONS : OUT_OF_FORMAT_DIMENSIONS

export const tabOfDimension = (dimension?: string | null): ClassificationTab =>
	dimension?.startsWith("X") ? "outOfFormat" : "bag"

export const getOrderDimension = (orderFlow: any): string | null =>
	orderFlow?.boxes?.[0]?.id?.substring(0, 3) ||
	orderFlow?.bagId?.substring(0, 2) ||
	null

export const describeDimension = (dimension?: string | null): string => {
	if (!dimension) return Strings.orders.unclassified
	const tab = tabOfDimension(dimension)
	const index = dimensionsFor(tab).indexOf(dimension)
	const tabLabel =
		tab === "bag" ? Strings.orders.tabBags : Strings.orders.tabOutOfFormat
	if (index === -1) return `${tabLabel} · ${dimension}`
	return `${tabLabel} · ${(Strings.orders as any)[SIZE_LABELS[index]]}`
}

// Upper volume (liters) of bag sizes #1–#4; anything bigger is #5.
const BAG_LITER_LIMITS = [0.05, 0.15, 0.3, 0.6]

// Port of PackageSize.volumeClassification() from the mobile app, using its
// 60×40×40 cm (70 L) standard box. Returns null when the order has no size.
export const suggestDimension = (packageSize: any): string | null => {
	const { width = 0, length = 0, height = 0 } = packageSize || {}
	if (!width || !length || !height) return null

	const liters = (width * length * height) / 1000
	const fitsStandardBox = width < 40 && length < 60 && height < 40

	if (fitsStandardBox) {
		const index = BAG_LITER_LIMITS.findIndex((limit) => liters <= limit)
		return BAG_DIMENSIONS[index === -1 ? BAG_DIMENSIONS.length - 1 : index]
	}
	const slots = Math.min(5, Math.max(1, Math.ceil(liters / 70)))
	return OUT_OF_FORMAT_DIMENSIONS[slots - 1]
}

interface ClassificationPresetSelectorProps {
	value: string
	onChange: (dimension: string) => void
	disabled?: boolean
}

const ClassificationPresetSelector: React.FC<
	ClassificationPresetSelectorProps
> = ({ value, onChange, disabled }) => {
	const tab = tabOfDimension(value)
	const dimensions = dimensionsFor(tab)
	const index = Math.max(0, dimensions.indexOf(value))

	const marks = Object.fromEntries(
		SIZE_LABELS.map((label, i) => [
			i,
			{
				style: { fontSize: 11, whiteSpace: "nowrap" as const },
				label: (Strings.orders as any)[label],
			},
		])
	)

	return (
		<div className="ClassificationPresetSelector">
			<Radio.Group
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					width: "100%",
				}}
				value={tab}
				disabled={disabled}
				onChange={(e) => onChange(dimensionsFor(e.target.value)[index])}
			>
				<Radio.Button value="bag" style={{ textAlign: "center" }}>
					{Strings.orders.tabBags}
				</Radio.Button>
				<Radio.Button value="outOfFormat" style={{ textAlign: "center" }}>
					{Strings.orders.tabOutOfFormat}
				</Radio.Button>
			</Radio.Group>
			<div style={{ padding: "8px 24px 16px" }}>
				<Slider
					min={0}
					max={dimensions.length - 1}
					step={null}
					marks={marks}
					value={index}
					disabled={disabled}
					tipFormatter={(i) => (i === undefined ? "" : dimensions[i])}
					onChange={(i: number) => onChange(dimensions[i])}
				/>
			</div>
		</div>
	)
}

export default ClassificationPresetSelector
